/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { PanelSplitter, SideNav } from '@atlaskit/navigation-system';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		// height of the root must be 100% to be contained correctly within all experiences
		height: '100%',
		/**
		 * rovo-navigation
		 * use 'inherit' to become the width of the rovo nav which should always have a width
		 * if width was 100% then our right padding values in each menu item would not apply
		 */
		width: 'inherit',
		overflow: 'auto',
		backgroundColor: token('elevation.surface'),
	},
	// Applied on top of `root` when rendered inside the chat mini-modal.
	rootMiniModal: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.xxlarge'),
	},
	flexContainer: {
		// This element controls the flex layout to position the slot elements correctly.
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
});

export const NAV_COLLAPSED_KEY = 'is-rovo-sidenav-collapsed';
const NAV_WIDTH_KEY = 'rovo-sidenav-width';
const DEFAULT_SIDE_NAV_WIDTH = 320;

const getSideNavWidth = () => {
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
	const width = window.localStorage.getItem(NAV_WIDTH_KEY);
	return (width && Number.parseInt(width)) || DEFAULT_SIDE_NAV_WIDTH;
};
// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
const isSideNavCollapsed = () => window.localStorage.getItem(NAV_COLLAPSED_KEY) === 'true';

export const SideNavContainer = ({
	testId,
	children,
	maxWidth,
	maxHeight,
	zIndex,
	isMiniModal = false,
	isADSNavigationEnabled,
}: {
	testId?: string;
	children: React.ReactElement[] | React.ReactElement;
	maxWidth?: string;
	maxHeight?: string;
	zIndex?: number;
	/**
	 * When true, applies the chat mini-modal styling (raised surface background + xxlarge
	 * border radius) on top of the default surface styling.
	 */
	isMiniModal?: boolean;
	isADSNavigationEnabled: boolean;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const defaultWidth = getSideNavWidth();
	const defaultCollapsed = isSideNavCollapsed();

	const onToggle = (isCollapsed: boolean) => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.setItem(NAV_COLLAPSED_KEY, isCollapsed.toString());
	};

	const onResize = (options: { finalWidth: number }) => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.setItem(NAV_WIDTH_KEY, String(options.finalWidth));
	};

	return isADSNavigationEnabled ? (
		<SideNav
			defaultWidth={defaultWidth}
			onCollapse={() => onToggle(true)}
			onExpand={() => onToggle(false)}
			defaultCollapsed={defaultCollapsed}
			testId={testId}
		>
			{children}
			<PanelSplitter label={formatMessage(messages.resizeSideNavLabel)} onResizeEnd={onResize} />
		</SideNav>
	) : (
		<div
			data-testid={testId}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			style={{ maxWidth, maxHeight, zIndex }}
			css={[styles.root, isMiniModal && styles.rootMiniModal]}
		>
			<div css={styles.flexContainer}>{children}</div>
		</div>
	);
};
